import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Users, Shield, Megaphone, ArrowLeft } from 'lucide-react';
import { useTeamStore } from '../store/teamStore';
import { supabase } from '../lib/supabase';
import { personaService, type Persona } from '../services/personaService';
import type { Sport } from '../types/core';
import { INDIVIDUAL_SPORTS, FORMAT_BY_SPORT, getEventLabel } from '../data/sportRulesets';

interface PersonaOption {
  id: Persona;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

const PERSONA_OPTIONS: PersonaOption[] = [
  {
    id: 'individual',
    icon: Users,
    title: "I'm a parent or player",
    description: "Track your own (or your player's) games — no team setup needed.",
  },
  {
    id: 'coach',
    icon: Shield,
    title: "I'm a coach",
    description: 'Set up a team, roster, practices, and games.',
  },
  {
    id: 'organizer',
    icon: Megaphone,
    title: "I'm an organizer",
    description: 'Running events or tournaments. Full organizer tools are coming soon.',
  },
];

type Step = 'persona' | 'individual-details';

/**
 * First screen for a brand-new user (or anyone who reaches a "no teams yet"
 * page — see NoTeamsGate). Picks the account's persona, which decides what
 * happens next: a coach or organizer goes to the existing /team-setup flow;
 * a parent/player is asked which sport to set up (Tennis by default, since
 * that's the only sport with a full Match Center today, but not hardcoded),
 * then gets a personal, single-player team created silently, with no
 * team-management chrome to deal with.
 */
const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const { createTeam } = useTeamStore();
  const [step, setStep] = useState<Step>('persona');
  const [selected, setSelected] = useState<Persona>('coach');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [sport, setSport] = useState<Sport>('Tennis');
  const [submitting, setSubmitting] = useState(false);

  const handlePersonaContinue = async () => {
    if (selected !== 'individual') {
      setSubmitting(true);
      try {
        await personaService.setPersona(selected);
        if (selected === 'organizer') {
          toast.success("You're set up as an organizer for now — full event tools are coming soon.");
        }
        navigate('/team-setup');
      } catch (error) {
        console.error('[Welcome] Error setting persona:', error);
        toast.error('Something went wrong. Please try again.');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setStep('individual-details');
  };

  const handleIndividualSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Please enter the player's name");
      return;
    }

    setSubmitting(true);
    try {
      await personaService.setPersona('individual');

      // Silently create a personal team of one and drop straight into the dashboard.
      // Mirrors TeamSetup.tsx's initial field set rather than relying on DB column
      // defaults for NOT NULL columns (time_format, player_sort_by) — those defaults
      // don't always apply depending on how the insert payload is built.
      await createTeam({
        name: `${firstName.trim()} ${lastName.trim()}`,
        sport,
        ageLevel: 'HS',
        experienceLevel: 'Intermediate',
        rosterSize: 1,
        format: FORMAT_BY_SPORT[sport],
        timeFormat: '12h',
        playerSortBy: 'firstName',
        isPersonal: true,
      });

      const newTeam = useTeamStore.getState().currentTeam;
      if (newTeam) {
        const { error: playerError } = await supabase.from('players').insert({
          team_id: newTeam.id,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          position: sport === 'Tennis' ? 'S1' : 'Player',
          experience: 'Intermediate',
        });
        if (playerError) {
          console.error('[Welcome] Error creating personal team player:', playerError);
        }
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('[Welcome] Error completing persona setup:', error);
      toast.error('Something went wrong setting up your account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'individual-details') {
    return (
      <div className="max-w-lg mx-auto px-4 py-10">
        <button
          type="button"
          onClick={() => setStep('persona')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1 className="text-h2 text-center mb-2">Set up your account</h1>
        <p className="text-center caption mb-8">Whose {getEventLabel(sport).plural.toLowerCase()} are we tracking, and in what sport?</p>

        <div className="mb-6 grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="welcome-first-name" className="block text-sm font-medium text-gray-700 mb-1">
              First name
            </label>
            <input
              id="welcome-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Jane"
            />
          </div>
          <div>
            <label htmlFor="welcome-last-name" className="block text-sm font-medium text-gray-700 mb-1">
              Last name
            </label>
            <input
              id="welcome-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Smith"
            />
          </div>
        </div>

        <div className="mb-8">
          <label htmlFor="welcome-sport" className="block text-sm font-medium text-gray-700 mb-1">
            Sport
          </label>
          <select
            id="welcome-sport"
            value={sport}
            onChange={(e) => setSport(e.target.value as Sport)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {INDIVIDUAL_SPORTS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleIndividualSubmit}
          disabled={submitting}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Setting up...' : 'Get Started'}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-10">
      <h1 className="text-h2 text-center mb-2">Welcome to Hustli</h1>
      <p className="text-center caption mb-8">Tell us who you are so we can set things up right.</p>

      <div className="space-y-3 mb-6">
        {PERSONA_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = selected === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setSelected(option.id)}
              aria-pressed={isSelected}
              className={`w-full text-left border rounded-xl p-4 flex items-start gap-3 transition-colors ${
                isSelected ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Icon className={`h-6 w-6 flex-shrink-0 mt-0.5 ${isSelected ? 'text-primary' : 'text-gray-400'}`} />
              <div>
                <div className="font-semibold text-gray-900">{option.title}</div>
                <div className="text-sm text-gray-500">{option.description}</div>
              </div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handlePersonaContinue}
        disabled={submitting}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Setting up...' : 'Continue'}
      </button>
    </div>
  );
};

export default Welcome;
